// Reglas de la lista de Inventario v3 (mock «Portal Admin VL v3»): lógica pura, sin React.
import type {
    InventoryLensCoverage,
    InventoryLensListItem,
    InventoryLensPeriod,
    ItemType,
    ProductCategoryWire,
    ProductTier,
} from '@/api/services/retail-catalog/backoffice';
import { dayOffset } from '@/lib/dateHelpers';
import { reportPeriodDays } from '@/shared/patterns/PeriodBand';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { productKindOf, type ProductKind } from '@/shared/patterns/KindChip';
import { KIND_LABEL } from './inventoryFormat';

/** Chips de tipo del mock más Kit y Servicio. SIM y módem comparten chip; Prepago no tiene datos. */
export type { ProductKind };

/** Chips de gama del mock. */
export type TierKey = 'HIGH' | 'MID' | 'LOW' | 'NONE';

/** Tarjetas de conteo de arriba de la tabla. */
export type StockBucket = 'ALL' | 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'SLOW_MOVER';

/** Columnas de la tabla, todas ordenables. */
export type SortColumn =
    | 'name'
    | 'sku'
    | 'kind'
    | 'stock'
    | 'sales'
    | 'coverage'
    | 'price'
    | 'alerts';

export interface SortState {
    column: SortColumn;
    direction: 'asc' | 'desc';
}

export interface InventoryRowAlerts {
    outOfStock: boolean;
    lowStock: boolean;
    slowMover: boolean;
    /** Ventas de 30 días en el percentil 90 del tenant. */
    topRotation: boolean;
}

/** Stock y ventas del periodo de una tienda; `sold` es `null` sin fuente de ventas (tarjetas PCF). */
export interface InventoryRowStore {
    storeId: string;
    stock: number;
    sold: number | null;
    /** Apartadas de la misma respuesta de la lista; ausente sin fuente (tarjetas PCF). */
    reserved?: number;
}

export interface InventoryRow {
    sku: string;
    brand: string;
    model: string;
    kind: ProductKind;
    tier: TierKey;
    stock: number;
    byStore: InventoryRowStore[];
    storesWithStock: number;
    /** Ventas del periodo elegido; `null` cuando no hay fuente de ventas (tarjetas PCF). */
    sales: number | null;
    coverage: InventoryLensCoverage;
    alerts: InventoryRowAlerts;
    /** `null` cuando no hay precio (tarjetas PCF). */
    priceCents: number | null;
    /** `null` o ausente = la fuente de promociones no respondió. */
    hasPromotion?: boolean | null;
    promotionNames?: readonly string[] | null;
}

export interface InventoryFilters {
    query: string;
    kinds: ProductKind[];
    tiers: TierKey[];
    bucket: StockBucket;
}

export const EMPTY_FILTERS: InventoryFilters = { query: '', kinds: [], tiers: [], bucket: 'ALL' };

/** Filtros de arranque: el deep link `?tab=critico` de Home VL abre con «Stock bajo». */
export function initialFilters(tab: string | undefined): InventoryFilters {
    return tab === 'critico' ? { ...EMPTY_FILTERS, bucket: 'LOW_STOCK' } : EMPTY_FILTERS;
}

export const DEFAULT_SORT: SortState = { column: 'sales', direction: 'desc' };

/** SKU con el que se lista el stock de tarjetas PCF de `retail-cards`. */
export const CARD_SKU = 'PCF';

const TIER_KEY: Record<ProductTier, TierKey> = {
    PREMIUM: 'HIGH',
    HIGH: 'HIGH',
    MID: 'MID',
    LOW: 'LOW',
};

/** Un servicio se reconoce por `itemType`, no por su categoría; el kit por `BUNDLE`. */
export function kindOf(category: ProductCategoryWire, itemType?: ItemType): ProductKind {
    return productKindOf(category, itemType);
}

/** Marca y modelo; un kit o un servicio sin ellos se nombra por su SKU. */
export function displayName(row: Pick<InventoryRow, 'sku' | 'brand' | 'model'>): string {
    return itemDisplayName(row);
}

/** Un servicio no lleva inventario: sin stock, piezas ni distribución. */
export function tracksStock(row: Pick<InventoryRow, 'kind'>): boolean {
    return row.kind !== 'SERVICE';
}

export function tierOf(tier: ProductTier | null): TierKey {
    return tier === null ? 'NONE' : TIER_KEY[tier];
}

function countWithStock(byStore: { stock: number }[]): number {
    return byStore.filter((s) => s.stock > 0).length;
}

export function toInventoryRow(item: InventoryLensListItem): InventoryRow {
    const byStore = item.byStore.map(({ storeId, stock, sold, reserved }) => ({
        storeId,
        stock,
        sold,
        ...(typeof reserved === 'number' ? { reserved } : {}),
    }));
    return {
        sku: item.sku,
        brand: item.brand ?? '',
        model: item.model ?? '',
        kind: kindOf(item.category, item.itemType),
        tier: tierOf(item.tier),
        stock: item.stockTotal,
        byStore,
        storesWithStock: countWithStock(byStore),
        sales: item.soldPeriod,
        coverage: item.coverage,
        alerts: {
            outOfStock: item.alerts.outOfStock,
            lowStock: item.alerts.lowStock,
            slowMover: item.alerts.slowMover,
            topRotation: item.alerts.topRotation,
        },
        priceCents: item.basePriceCents ?? null,
        hasPromotion: item.hasPromotion ?? null,
        promotionNames: item.promotionNames ?? null,
    };
}

/** Fila de tarjetas PCF: solo stock por tienda; ventas y precio no tienen fuente. */
export function cardRow(stockByStore: ReadonlyMap<string, number>, storeId?: string): InventoryRow {
    const byStore: InventoryRowStore[] = storeId
        ? [{ storeId, stock: stockByStore.get(storeId) ?? 0, sold: null }]
        : [...stockByStore].map(([id, stock]) => ({ storeId: id, stock, sold: null }));
    const stock = byStore.reduce((total, s) => total + s.stock, 0);
    return {
        sku: CARD_SKU,
        brand: 'Tarjeta',
        model: 'PCF',
        kind: 'CARD',
        tier: 'NONE',
        stock,
        byStore,
        storesWithStock: countWithStock(byStore),
        sales: null,
        coverage: null,
        alerts: { outOfStock: stock === 0, lowStock: false, slowMover: false, topRotation: false },
        priceCents: null,
    };
}

function inBucket(row: InventoryRow, bucket: StockBucket): boolean {
    switch (bucket) {
        case 'ALL':
            return true;
        case 'IN_STOCK':
            return row.stock > 0;
        case 'LOW_STOCK':
            return row.alerts.lowStock;
        case 'OUT_OF_STOCK':
            return tracksStock(row) && row.stock === 0;
        case 'SLOW_MOVER':
            return row.alerts.slowMover;
    }
}

/** Conteo de cada tarjeta sobre todas las filas, antes de búsqueda, tipo y gama. */
export function bucketCounts(rows: readonly InventoryRow[]): Record<StockBucket, number> {
    const count = (bucket: StockBucket) => rows.filter((r) => inBucket(r, bucket)).length;
    return {
        ALL: rows.length,
        IN_STOCK: count('IN_STOCK'),
        LOW_STOCK: count('LOW_STOCK'),
        OUT_OF_STOCK: count('OUT_OF_STOCK'),
        SLOW_MOVER: count('SLOW_MOVER'),
    };
}

function matchesQuery(row: InventoryRow, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${row.sku} ${row.brand} ${row.model}`.toLowerCase().includes(q);
}

/** Filtros puestos en el panel: búsqueda, cada chip de tipo y gama, y la tienda. */
export function activeFilterCount(filters: InventoryFilters, storeId: string): number {
    return (
        (filters.query.trim() === '' ? 0 : 1) +
        filters.kinds.length +
        filters.tiers.length +
        (storeId === '' ? 0 : 1)
    );
}

/** Si la fila pasa búsqueda, tipo, gama y tarjeta de conteo. */
export function matchesFilters(row: InventoryRow, filters: InventoryFilters): boolean {
    return (
        matchesQuery(row, filters.query) &&
        (filters.kinds.length === 0 || filters.kinds.includes(row.kind)) &&
        (filters.tiers.length === 0 || filters.tiers.includes(row.tier)) &&
        inBucket(row, filters.bucket)
    );
}

const TEXT_COLUMNS: ReadonlySet<SortColumn> = new Set(['name', 'sku', 'kind']);

/** Otra columna arranca descendente (las de texto, ascendentes); la misma alterna. */
export function nextSort(current: SortState, column: SortColumn): SortState {
    if (current.column === column) {
        return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { column, direction: TEXT_COLUMNS.has(column) ? 'asc' : 'desc' };
}

export type RowAlertKey = 'out' | 'low' | 'slow';

export interface RowAlert {
    key: RowAlertKey;
    label: string;
}

/** Alertas de la tabla de la más grave a la menos grave; top rotación no es alerta. */
export function rowAlerts(row: Pick<InventoryRow, 'alerts'>): RowAlert[] {
    const { outOfStock, lowStock, slowMover } = row.alerts;
    const alerts: RowAlert[] = [];
    if (outOfStock) alerts.push({ key: 'out', label: 'Sin stock' });
    if (lowStock) alerts.push({ key: 'low', label: 'Stock bajo' });
    if (slowMover) alerts.push({ key: 'slow', label: 'Lento' });
    return alerts;
}

function alertRank(alerts: InventoryRowAlerts): number {
    if (alerts.outOfStock) return 3;
    if (alerts.lowStock) return 2;
    if (alerts.slowMover) return 1;
    return 0;
}

function coverageRank(coverage: InventoryLensCoverage): number {
    if (coverage === null) return 0;
    return coverage === 'INFINITE' ? Number.POSITIVE_INFINITY : coverage;
}

/** Valor por el que ordena cada columna; `null` cuando la fila no tiene ese dato. */
export function sortValueOf(row: InventoryRow, column: SortColumn): number | string | null {
    switch (column) {
        case 'name':
            return displayName(row).toLowerCase();
        case 'sku':
            return row.sku.toLowerCase();
        case 'kind':
            return KIND_LABEL[row.kind].toLowerCase();
        case 'stock':
            return tracksStock(row) ? row.stock : null;
        case 'sales':
            return row.sales;
        case 'coverage':
            return coverageRank(row.coverage);
        case 'price':
            return row.priceCents;
        case 'alerts':
            return alertRank(row.alerts);
    }
}

/** Compara dos filas por una columna, ascendente; lo que no tiene dato va al final. */
export function compareRows(a: InventoryRow, b: InventoryRow, column: SortColumn): number {
    const av = sortValueOf(a, column);
    const bv = sortValueOf(b, column);
    if (av === null || bv === null) return av === bv ? 0 : av === null ? 1 : -1;
    if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv, 'es');
    if (av < bv) return -1;
    if (av > bv) return 1;
    return 0;
}

export type CoverageTone = 'danger' | 'warning' | 'success' | 'muted' | 'none';

/** Texto y color de la cobertura, con los umbrales del mock (7, 15 y 30 días). */
export function coverageDisplay(
    coverage: InventoryLensCoverage,
    stock: number,
): { text: string; tone: CoverageTone } {
    if (stock === 0 || coverage === null) return { text: '—', tone: 'none' };
    if (coverage === 'INFINITE') return { text: '∞', tone: 'none' };
    const text = `${String(Math.round(coverage))}d`;
    if (coverage < 7) return { text, tone: 'danger' };
    if (coverage < 15) return { text, tone: 'warning' };
    if (coverage < 30) return { text, tone: 'success' };
    return { text, tone: 'muted' };
}

/** Rango del filtro de ventas del SKU; hoy es el último día, como en la lente. `null` en todo el historial. */
export function periodRange(
    period: InventoryLensPeriod,
    today: string,
): { from: string; to: string } | null {
    const days = reportPeriodDays(period);
    return days === null ? null : { from: dayOffset(today, -(days - 1)), to: today };
}

/** Agrega o quita un valor de un filtro de chips. */
export function toggleValue<T>(list: readonly T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** En consolidado la distribución lista todas las tiendas del retailer, como el mock. */
function withAllStores(row: InventoryRow, storeIds: readonly string[]): InventoryRow {
    if (storeIds.length === 0 || !tracksStock(row)) return row;
    const byId = new Map(row.byStore.map((s) => [s.storeId, s]));
    const emptySold = row.kind === 'CARD' ? null : 0;
    const extra = row.byStore.filter((s) => !storeIds.includes(s.storeId));
    const byStore = [
        ...storeIds.map((id) => byId.get(id) ?? { storeId: id, stock: 0, sold: emptySold }),
        ...extra,
    ];
    return { ...row, byStore };
}

/** Filas de la tabla: las del lente más la de tarjetas PCF, si se pudo leer su saldo. */
export function buildInventoryRows(
    items: readonly InventoryLensListItem[],
    cardStock: ReadonlyMap<string, number> | undefined,
    storeIds: readonly string[],
    storeId: string | undefined,
): InventoryRow[] {
    const rows = items.map(toInventoryRow);
    if (cardStock) rows.push(cardRow(cardStock, storeId));
    return storeId ? rows : rows.map((r) => withAllStores(r, storeIds));
}
